import * as React from 'react';
import { Formik, Form } from 'formik';
import Grid from '@mui/material/Unstable_Grid2';

import { currencyMask, numberMask } from 'helpers/masks';
import { TextInput } from 'components/Input';
import { Spacing } from 'components/Layout';
import LrDialog from 'components/LrDialog';
import LrButton from 'components/LrButton';
import { getProductLineTypesByCode } from 'application/redux/selectors';
import { useSelector } from 'react-redux';
import { Typography } from '@mui/material';
import { currencyToNumber, formatDollar } from '../../../../helpers/formatters';
import { UnderwritingReviewRequest } from '../../../../types/underwriting.type';
import * as Yup from 'yup';
import { getProductLineDisplayValue } from '../../../../helpers/submissions';
import _ from 'lodash';

interface Props {
    quotedLines: Record<string, { reviewPayload: UnderwritingReviewRequest }>;
    onCancel: () => void;
    onSubmit: (payload: UnderwritingReviewRequest[]) => void;
    defaultAgentCommission?: number | string;
}

const FeesDialog: React.FunctionComponent<Props> = (props) => {
    const { onCancel, onSubmit, quotedLines, defaultAgentCommission } = props;
    const productLineTypes = useSelector(getProductLineTypesByCode);
    const initialValues: Record<
        string,
        { policyFee: string | null; professionalFee: string | null; agentCommission: string | null }
    > = {};
    let numberOfLines = 0;
    Object.keys(quotedLines).forEach((key: string) => {
        numberOfLines += 1;
        initialValues[key] = {
            policyFee: String(Math.ceil((quotedLines[key].reviewPayload.totalPremium ?? 0) * 0.03)),
            professionalFee: null,
            agentCommission: null,
        };
    });

    // ValidationSchema
    const feesSchemaPart = (maxCommission = 13) =>
        Yup.object().shape({
            agentCommission: Yup.number()
                .max(maxCommission, `Commission must not exceed ${_.round(maxCommission, 1)}%`)
                .min(0, 'Commission must above 0')
                .notRequired()
                .nullable(true),
        });
    const feeSchemaDef = {};
    Object.keys(quotedLines).forEach((productCode) => {
        console.log('payload: ', quotedLines[productCode].reviewPayload);
        return (feeSchemaDef[productCode] = feesSchemaPart(
            quotedLines[productCode].reviewPayload.maximumBrokerCommissionPercentage
        ));
    });
    const feeSchema = Yup.object().shape(feeSchemaDef);

    const submitForm = (
        formValues: Record<
            string,
            { policyFee: string | null; professionalFee: string | null; agentCommission: string | null }
        >
    ) => {
        const reviewPayload: UnderwritingReviewRequest[] = [];
        Object.keys(formValues).forEach((key: string) => {
            const review = quotedLines[key].reviewPayload;
            const formValue = formValues[key];
            review.policyFee = currencyToNumber(formValue?.policyFee || '0');
            review.professionalFee = currencyToNumber(formValue?.professionalFee || '0');
            review.agentCommissionOverride =
                formValue?.agentCommission != null ? Number(formValue.agentCommission) / 100.0 : undefined;
            reviewPayload.push(review);
        });
        !!onSubmit && onSubmit(reviewPayload);
    };
    return (
        <LrDialog isOpen title="Enter Fees" width={String(numberOfLines === 1 ? 332 : numberOfLines * 258) + 'px'}>
            <Formik
                initialValues={initialValues}
                isInitialValid={true}
                onSubmit={submitForm}
                validationSchema={feeSchema}
            >
                {({ handleBlur, handleChange, isSubmitting, isValid, values, touched, errors }) => (
                    <Form>
                        <Grid container>
                            {Object.keys(values).map((key) => (
                                <Grid>
                                    <Typography variant={'h4'}>
                                        {getProductLineDisplayValue(productLineTypes, key)}
                                    </Typography>
                                    <Typography>{`Premium: ${formatDollar(
                                        quotedLines[key].reviewPayload.totalPremium ?? 0
                                    )}`}</Typography>
                                    <TextInput
                                        title="Policy Fee"
                                        name={`${key}.policyFee`}
                                        placeholder="0.00"
                                        value={values[key].policyFee}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        mask={currencyMask}
                                    />
                                    {/*<Spacing height="24px" />*/}
                                    {/*<TextInput*/}
                                    {/*    title="Professional Fee"*/}
                                    {/*    name={`${key}.professionalFee`}*/}
                                    {/*    placeholder="0.00"*/}
                                    {/*    value={values[key].professionalFee}*/}
                                    {/*    onChange={handleChange}*/}
                                    {/*    onBlur={handleBlur}*/}
                                    {/*    mask={currencyMask}*/}
                                    {/*/>*/}
                                    <Spacing height="24px" />
                                    <TextInput
                                        title="Agent Commission"
                                        name={`${key}.agentCommission`}
                                        placeholder={defaultAgentCommission ? defaultAgentCommission.toString() : '0'}
                                        endAdornment={'%'}
                                        value={values[key].agentCommission}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        errorMessage={touched[key]?.agentCommission && errors[key]?.agentCommission}
                                        mask={numberMask}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                        <Spacing height="32px" />
                        <Grid container spacing={0} alignItems="center">
                            <LrButton lrType="text" onClick={onCancel}>
                                Cancel
                            </LrButton>
                            <LrButton lrType="primary" type="submit" disabled={!isValid} isLoading={isSubmitting}>
                                Next
                            </LrButton>
                        </Grid>
                    </Form>
                )}
            </Formik>
        </LrDialog>
    );
};

export default FeesDialog;
